/**
 * BackgroundMusicField — admin template editor picker for the deck's
 * background music.
 *
 * The picker surfaces a dropdown of the in-built `/audio/*` tracks
 * (the same 6 FREE + 9 PRO tracks exposed to the public wizard) plus
 * a free-form URL fallback for AI-generated music. The selection
 * lives in the parent template's `musicUrl` + `musicType` fields so
 * the ProposalPreview modal can play it immediately and the seed
 * carries it into every proposal that uses the template.
 */
import { useEffect, useState } from 'react';
import { listPlatformAudio } from '@/lib/audio';
import { cn } from '@/lib/utils';

const INBUILT_PREFIX = '/audio/';

interface BackgroundMusicFieldProps {
  url: string | null;
  type: string | null;
  onChange: (url: string | null, type: string | null) => void;
}

interface TrackRow {
  id: string;
  title: string;
  artist: string | null;
  url: string;
  isPro: boolean;
}

export function BackgroundMusicField({ url, type, onChange }: BackgroundMusicFieldProps) {
  const [tracks, setTracks] = useState<TrackRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await listPlatformAudio({ limit: 100 });
        if (cancelled) return;
        // The admin picker only needs the in-built tracks (those that
        // resolve to a /audio/* URL). AI-generated tracks carry a
        // `metadata.kind` flag in production, but for the picker we
        // also let the user paste a custom URL below.
        const rows: TrackRow[] = (res.tracks ?? [])
          .filter((t) => typeof t.url === 'string' && t.url.startsWith(INBUILT_PREFIX))
          .map((t) => ({
            id: t.id,
            title: t.title,
            artist: t.artist,
            url: t.url,
            isPro: t.tier === 'PRO',
          }));
        setTracks(rows);
        setError(null);
      } catch (e) {
        if (!cancelled) setError((e as Error)?.message ?? 'Failed to load tracks');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const currentUrl = url ?? '';
  const currentTrack = tracks.find((t) => t.url === currentUrl);
  const isCustom = !!url && !currentTrack && !url.startsWith(INBUILT_PREFIX);

  return (
    <div className="space-y-2">
      <p className="lbl">Background music</p>
      <p className="text-xs text-white/60">
        Plays behind the deck so the proposee hears the chosen track as the slides advance. The preview
        modal auditions the selection in real time.
      </p>
      <select
        className="inp cursor-pointer"
        value={isCustom ? '__custom__' : currentUrl}
        onChange={(e) => {
          const value = e.target.value;
          if (value === '') {
            onChange(null, null);
            return;
          }
          if (value === '__custom__') {
            // Keep whatever URL the user had pasted, but ensure it's
            // reflected as "custom" rather than re-routed into a code
            // match. The custom input below lets them edit it.
            onChange(url && !url.startsWith(INBUILT_PREFIX) ? url : '', 'custom');
            return;
          }
          const track = tracks.find((t) => t.url === value);
          onChange(value, track?.title ?? 'custom');
        }}
      >
        <option value="">No music</option>
        {tracks.map((t) => (
          <option key={t.id} value={t.url}>
            {t.isPro ? '⭐ ' : ''}{t.title}{t.artist ? ` — ${t.artist}` : ''}{t.isPro ? ' (PRO)' : ''}
          </option>
        ))}
        {url && !currentTrack && !url.startsWith(INBUILT_PREFIX) ? (
          <option value="__custom__">Custom URL…</option>
        ) : null}
      </select>
      {isCustom || (url && !url.startsWith(INBUILT_PREFIX) && !currentTrack) ? (
        <input
          type="url"
          className="inp"
          placeholder="https://cdn.example.com/track.mp3"
          value={url ?? ''}
          onChange={(e) => onChange(e.target.value || null, e.target.value ? 'custom' : null)}
        />
      ) : null}
      {loading ? <p className="text-xs text-white/50">Loading in-built tracks…</p> : null}
      {error ? <p className="text-xs text-red-300">{error}</p> : null}
      {url ? (
        <p className={cn('text-xs', currentTrack?.isPro ? 'text-amber-300' : 'text-emerald-300')}>
          {type ?? 'custom'} selected — preview modal will play this.
        </p>
      ) : null}
    </div>
  );
}
